import { useState, useEffect } from 'react';
import { api } from '../api/client';

export function PromptForgePage() {
    const [domainMode, setDomainMode] = useState<'select' | 'manual'>('select');
  const [domainId, setDomainId] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ is_safe: boolean; updated_prompt?: string; violation_reason?: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [feedbacksList, setFeedbacksList] = useState<any[]>([]);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      const res = await api.adminGetFeedbacks();
      if (res.feedbacks) setFeedbacksList(res.feedbacks);
    } catch (e) {
      console.error("Failed to load feedbacks", e);
    }
  };

  const handleConvert = async () => {
    if (!domainId || !feedback) {
      setErrorMsg('Vui lòng điền đủ Domain ID và Nội dung Prompt/Feedback.');
      return;
    }
    setErrorMsg('');
    setResult(null);
    setLoading(true);

    try {
      const res = await api.adminConvertPromptForge(domainId, feedback);
      setResult(res);
    } catch (e: any) {
      setErrorMsg(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Prompt Forge (RLHF)</h1>
          <p className="page-subtitle">The AI Factory: Audit & Convert Feedback to Registry Pattern using Cloudflare gpt-oss-120b</p>
        </div>
      </div>

      <div className="grid-2" style={{ gap: '24px', alignItems: 'flex-start' }}>
        {/* Left Column: Config */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <div className="card__title">⚙️ Cấu hình Engine</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Domain ID (Target Node)</span>
                  <span 
                    style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontSize: '12px' }}
                    onClick={() => setDomainMode(m => m === 'select' ? 'manual' : 'select')}
                  >
                    {domainMode === 'select' ? 'Nhập thủ công' : 'Chọn từ danh sách'}
                  </span>
                </label>
                
                {domainMode === 'select' ? (
                  <select 
                    className="input" 
                    value={domainId} 
                    onChange={e => setDomainId(e.target.value)}
                  >
                    <option value="">-- Chọn lĩnh vực (Tự động map ID) --</option>
                    <option value="lane_4_legal">⚖️ Pháp lý (lane_4_legal)</option>
                    <option value="lane_4_medical">🏥 Y tế & Sức khoẻ (lane_4_medical)</option>
                    <option value="lane_4_academic">📚 Khoa học & Học thuật (lane_4_academic)</option>
                    <option value="lane_4_agent">📈 Tài chính / Công nghệ / Khác (lane_4_agent)</option>
                    <option value="gateway_router">🔀 Gateway Router (gateway_router)</option>
                    <option value="lane_0_simple_qa">💬 Linguistic Chat (lane_0_simple_qa)</option>
                  </select>
                ) : (
                  <input 
                    type="text" 
                    className="input" 
                    placeholder="Ví dụ: lane_4_crypto" 
                    value={domainId} 
                    onChange={e => setDomainId(e.target.value)} 
                  />
                )}
              </div>
            </div>
          </div>

          <div className="card card--accent">
            <div className="card__title">🛡️ Cơ chế Hoạt động</div>
            <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', paddingLeft: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <li><strong>Bảo vệ nội tại:</strong> Hệ thống dùng model <code style={{color:'var(--accent-primary)'}}>@cf/baai/bge-m3</code> để check Cosine Similarity. Nếu prompt của bạn quá giống domain hiện có {'>'} 85%, hệ thống sẽ từ chối ngay.</li>
              <li><strong>Audit Mã độc:</strong> Cloudflare gpt-oss-120b sẽ kiểm duyệt xem nội dung nhập vào có chứa "Jailbreak" hay không.</li>
              <li><strong>Biên dịch:</strong> Ép chuẩn hóa cấu trúc 3 phần (Phân tích {'>'} Tư duy {'>'} Hành động) trước khi lưu vào KV Cache.</li>
            </ul>
          </div>
        </div>

        {/* Right Column: The Forge */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <div className="card__title">🔥 Lò Rèn Prompt (Tình huống / Feedback)</div>
            <textarea 
              className="input" 
              style={{ minHeight: '180px', resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: '13px', lineHeight: 1.5 }}
              placeholder="Nhập tình huống hoặc nội dung feedback từ người dùng cần nâng cấp. Ví dụ: 'Khi người dùng hỏi về bất động sản, AI cần kiểm tra giá nhà nước trước rồi mới kiểm tra giá thị trường, bắt buộc dùng tool finance...'"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
            />
            
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button 
                className="btn btn--primary" 
                onClick={handleConvert} 
                disabled={loading || !domainId || !feedback}
                style={{ padding: '10px 24px', fontSize: '14px', fontWeight: 600 }}
              >
                {loading ? <span className="spinner" /> : '⚡ Convert to Registry Pattern'}
              </button>
            </div>
          </div>

          {/* Result Area */}
          {errorMsg && (
            <div className="card" style={{ borderColor: 'var(--color-danger)', background: 'rgba(239, 68, 68, 0.05)' }}>
              <div style={{ color: 'var(--color-danger)', fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>❌</span> Thất bại
              </div>
              <p style={{ color: 'var(--color-danger)', marginTop: '8px', fontSize: '13px' }}>{errorMsg}</p>
            </div>
          )}

          {result && (
            <div className="card" style={{ borderColor: result.is_safe ? 'var(--color-success)' : 'var(--color-danger)' }}>
              <div style={{ color: result.is_safe ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                {result.is_safe ? '✅ Audit Safety: PASSED | Pattern Saved to KV' : '❌ Audit Safety: BLOCKED'}
              </div>
              
              {!result.is_safe && (
                <div style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
                  <strong>Lý do từ chối:</strong> {result.violation_reason}
                </div>
              )}

              {result.is_safe && result.updated_prompt && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase' }}>
                    JSON Output (Optimized)
                  </div>
                  <pre style={{ 
                    background: 'var(--bg-elevated)', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    fontSize: '12px', 
                    color: '#a78bfa', 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word',
                    border: '1px solid var(--border-color)',
                    fontFamily: 'var(--font-mono)'
                  }}>
                    {result.updated_prompt}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Flagged Feedbacks Area */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card__title">🚩 Cảnh báo chất lượng (Disliked Sessions)</div>
        {feedbacksList.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Không có cảnh báo nào.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {feedbacksList.map(f => (
              <div key={f.id} style={{ 
                padding: '12px', background: 'var(--bg-elevated)', border: '1px solid var(--border-color)', borderRadius: '8px' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    <strong>Session:</strong> {f.session_id} | <strong>User:</strong> {f.display_name || f.email || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: '12px', color: f.score > 0 ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                    {f.score > 0 ? '👍 LIKE' : '👎 DISLIKE'}
                  </div>
                </div>
                {f.feedback_text && (
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginTop: '8px' }}>
                    <strong>Comment:</strong> {f.feedback_text}
                  </div>
                )}
                <div style={{ marginTop: '8px' }}>
                  <button 
                    className="btn btn--secondary" 
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                    onClick={() => {
                      setFeedback(`Sửa lỗi ở Session: ${f.session_id}.\nNội dung cần sửa: ...`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    🛠 Đưa vào Lò Rèn
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
